import React from "react"
import { Table } from "@tanstack/react-table"
import { TableColumnAlign, TableRowData, TableSize } from "../types"
import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { classNames } from "../../../../utils/classNames"
import { head } from "ramda"
import {
    flexRenderCustom,
    getCellHeightClasses,
    getFirstColButtonsWidth,
    getOuterColumnPaddingClasses,
} from "../styleUtils"

type Props<TRowData> = {
    table: Table<TRowData>
    size: TableSize
    rowClassName?: string
    bordered?: boolean
    virtualized?: boolean
}

export const TableFooter = <TRowData extends TableRowData>(props: Props<TRowData>) => {
    const columnAlignToClass = (align: Maybe<TableColumnAlign>): string => {
        switch (align) {
            case "left":
                return "text-left"
            case "center":
                return "text-center"
            case "right":
                return "text-right"
            default:
                return "text-left"
        }
    }

    return (
        <tfoot className={classNames(classes.footer, props.virtualized && classes.footerBaseVirtualized)}>
            {props.table.getFooterGroups().map(footerGroup => (
                <tr
                    key={footerGroup.id}
                    className={classNames(
                        getOuterColumnPaddingClasses(props.size).headerAndFooter,
                        props.virtualized && classes.rowBaseVirtualized,
                        props.bordered && classes.bordered,
                        props.rowClassName
                    )}>
                    {footerGroup.headers.map(header => {
                        const alignClass = columnAlignToClass(header.column.columnDef.meta?.align)

                        const isFirstColumn = head(props.table.getAllColumns())?.id === header.column.id
                        const isSelectCheckboxCol = !!(isFirstColumn && props.table.options.meta?.hasSelectableRows)

                        return (
                            <th
                                key={header.id}
                                colSpan={header.colSpan}
                                className={classNames(
                                    classes.footerCell,
                                    getCellHeightClasses(props.size),
                                    props.virtualized && classes.footerCellVirtualized,
                                    alignClass,
                                    header.column.columnDef.meta?.columnClassName
                                )}
                                style={{
                                    width: header.column.columnDef.meta?.widthPercentage ?? header.getSize(),
                                    maxWidth: header.column.columnDef.meta?.maxWidth,
                                }}>
                                {header.isPlaceholder ? null : (
                                    <div
                                        style={{
                                            // Apply paddingLeft if the cell has expand button, select checkbox, or is nested
                                            paddingLeft: getFirstColButtonsWidth(false, isSelectCheckboxCol, 0) + "px",
                                        }}>
                                        {flexRenderCustom(header.column.columnDef.footer, header.getContext())}
                                    </div>
                                )}
                            </th>
                        )
                    })}
                </tr>
            ))}
        </tfoot>
    )
}

const classes = {
    footer: "bg-[#F0F2F5] border-t border-gray-200/80",
    footerCell: "text-black/80",
    footerCellVirtualized: "flex",
    bordered: "[&>th]:border-r [&>th]:border-gray-200/80 [&>th:last-child]:border-r-0",
    rowBaseVirtualized: "w-full flex",
    footerBaseVirtualized: "w-full grid",
}
